/**
 * AstorContextProvider
 *
 * The client-side brain for the Astor onboarding system. Manages all Astor state
 * via useReducer, persists nudge dismissals to localStorage, and syncs activity
 * events to the server via callback props.
 *
 * This component does NOT make tRPC calls directly — instead it accepts callback
 * props that the portal provides, keeping the design system decoupled from
 * transport concerns.
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import type {
  AstorAction,
  AstorEvent,
  AstorEventType,
  AstorNudge,
  HighlightAction,
  OnboardingPlan,
  PageContextInput,
  SpotlightStep,
} from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import type { AstorContextProviderProps, AstorContextState, AstorContextValue } from './astor-context.types';
import { AstorContext } from './useAstorContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_MAX_NUDGES_PER_SESSION = 3;
const ACTIVITY_SYNC_INTERVAL_MS = 30_000;
const LOCALSTORAGE_PREFIX = 'astor-nudge-dismissed-';

// ============================================================================
// REDUCER
// ============================================================================

type AstorReducerAction =
  | { type: 'SET_PLAN'; plan: OnboardingPlan | null }
  | { type: 'SET_GENERATING'; isGenerating: boolean }
  | { type: 'COMPLETE_MILESTONE'; milestoneId: string }
  | { type: 'DISMISS_ONBOARDING' }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'TRACK_PAGE_VIEW'; route: string; timestamp: Date }
  | { type: 'UPDATE_PAGE_DURATION'; route: string; duration: number }
  | { type: 'TRACK_ACTION'; actionKey: string }
  | { type: 'TRACK_ERROR'; error?: string }
  | { type: 'SET_IDLE'; since: Date }
  | { type: 'RESET_IDLE' }
  | { type: 'DISMISS_NUDGE'; category: string }
  | { type: 'SHOW_NUDGE'; nudge: AstorNudge }
  | { type: 'CLEAR_NUDGE' }
  | { type: 'OPEN_ASTOR'; mode: 'widget' | 'fullpage' | 'palette' }
  | { type: 'CLOSE_ASTOR' }
  | { type: 'SET_PENDING_ACTION'; action: AstorAction | null }
  | { type: 'ADD_MESSAGE'; message: { id: string; role: 'user' | 'assistant'; content: string } }
  | { type: 'RESTORE_DISMISSED_NUDGES'; categories: string[] }
  | { type: 'SET_HIGHLIGHT'; highlight: HighlightAction }
  | { type: 'CLEAR_HIGHLIGHT' }
  | { type: 'SET_SPOTLIGHT_TOUR'; tourId: string; steps: SpotlightStep[]; milestoneId?: string }
  | { type: 'CLEAR_SPOTLIGHT_TOUR' }
  | { type: 'SET_SUGGESTED_ACTIONS'; actions: Array<{ label: string; action: AstorAction }> };

function computeCompletionPercentage(plan: OnboardingPlan | null, completed: Set<string>): number {
  if (!plan) return 0;
  const allMilestones = plan.sections.flatMap((s) => s.milestones);
  if (allMilestones.length === 0) return 0;
  return Math.round((completed.size / allMilestones.length) * 100);
}

function astorReducer(state: AstorContextState, action: AstorReducerAction): AstorContextState {
  switch (action.type) {
    case 'SET_PLAN': {
      const completedMilestones = new Set<string>();
      let persistedDismissed = false;
      if (action.plan) {
        // Restore from persisted completedMilestones array (from DB via API)
        if (Array.isArray(action.plan.completedMilestones)) {
          for (const id of action.plan.completedMilestones) {
            completedMilestones.add(id);
          }
        }
        // Also check milestone statuses in the plan JSON
        if (Array.isArray(action.plan.sections)) {
          for (const section of action.plan.sections) {
            for (const milestone of section.milestones ?? []) {
              if (milestone.status === 'completed') {
                completedMilestones.add(milestone.id);
              }
            }
          }
        }
        // Persisted dismissed flag from the server — without this, the rail
        // reappears on every page navigation because SET_PLAN would otherwise
        // overwrite an in-memory dismissed:true back to the default false.
        persistedDismissed = action.plan.dismissed === true;
      }
      // Preserve the in-memory dismissed flag only when the incoming plan is
      // the same plan the user already dismissed — otherwise a newly generated
      // plan (different id) would inherit the prior dismiss and be invisible
      // from the start. OR with state only on same-id refetch; drop state
      // otherwise so the new plan's server-side dismissed flag is authoritative.
      const samePlanId =
        action.plan !== null && state.onboarding.plan !== null && action.plan.id === state.onboarding.plan.id;
      const dismissed = persistedDismissed || (samePlanId && state.onboarding.dismissed);
      return {
        ...state,
        onboarding: {
          ...state.onboarding,
          plan: action.plan,
          completedMilestones,
          completionPercentage: computeCompletionPercentage(action.plan, completedMilestones),
          dismissed,
          isLoading: false,
          isGenerating: false,
        },
      };
    }

    case 'SET_GENERATING':
      return {
        ...state,
        onboarding: { ...state.onboarding, isGenerating: action.isGenerating },
      };

    case 'COMPLETE_MILESTONE': {
      const newCompleted = new Set(state.onboarding.completedMilestones);
      newCompleted.add(action.milestoneId);
      return {
        ...state,
        onboarding: {
          ...state.onboarding,
          completedMilestones: newCompleted,
          completionPercentage: computeCompletionPercentage(state.onboarding.plan, newCompleted),
        },
      };
    }

    case 'DISMISS_ONBOARDING':
      return {
        ...state,
        onboarding: { ...state.onboarding, dismissed: true },
      };

    case 'SET_LOADING':
      return {
        ...state,
        onboarding: { ...state.onboarding, isLoading: action.isLoading },
      };

    case 'TRACK_PAGE_VIEW': {
      const newPagesVisited = new Map(state.activity.pagesVisited);
      const existing = newPagesVisited.get(action.route);
      newPagesVisited.set(action.route, {
        count: (existing?.count ?? 0) + 1,
        lastVisit: action.timestamp,
        totalDuration: existing?.totalDuration ?? 0,
      });
      return {
        ...state,
        activity: {
          ...state.activity,
          currentRoute: action.route,
          pagesVisited: newPagesVisited,
          routeHistory: [
            ...state.activity.routeHistory,
            { path: action.route, timestamp: action.timestamp, duration: 0 },
          ],
        },
      };
    }

    case 'UPDATE_PAGE_DURATION': {
      const newPagesVisited = new Map(state.activity.pagesVisited);
      const existing = newPagesVisited.get(action.route);
      if (existing) {
        newPagesVisited.set(action.route, {
          ...existing,
          totalDuration: existing.totalDuration + action.duration,
        });
      }
      // Update the last routeHistory entry's duration if path matches
      let newRouteHistory = state.activity.routeHistory;
      if (newRouteHistory.length > 0) {
        const lastEntry = newRouteHistory[newRouteHistory.length - 1];
        if (lastEntry.path === action.route) {
          newRouteHistory = [...newRouteHistory];
          newRouteHistory[newRouteHistory.length - 1] = {
            ...lastEntry,
            duration: lastEntry.duration + action.duration,
          };
        }
      }
      return {
        ...state,
        activity: { ...state.activity, pagesVisited: newPagesVisited, routeHistory: newRouteHistory },
      };
    }

    case 'TRACK_ACTION': {
      const newActions = new Map(state.activity.actionsPerformed);
      newActions.set(action.actionKey, (newActions.get(action.actionKey) ?? 0) + 1);
      return {
        ...state,
        activity: { ...state.activity, actionsPerformed: newActions },
      };
    }

    case 'TRACK_ERROR':
      return {
        ...state,
        activity: {
          ...state.activity,
          errorsThisSession: state.activity.errorsThisSession + 1,
          errorsEncountered: [
            ...state.activity.errorsEncountered,
            { route: state.activity.currentRoute, error: action.error ?? 'unknown', timestamp: new Date() },
          ],
        },
      };

    case 'SET_IDLE':
      return {
        ...state,
        activity: { ...state.activity, idleSince: action.since },
      };

    case 'RESET_IDLE':
      return {
        ...state,
        activity: { ...state.activity, idleSince: null },
      };

    case 'DISMISS_NUDGE': {
      const newDismissed = new Set(state.nudges.dismissed);
      newDismissed.add(action.category);
      const newCooldown = new Map(state.nudges.cooldown);
      newCooldown.set(action.category, new Date());
      return {
        ...state,
        nudges: {
          ...state.nudges,
          dismissed: newDismissed,
          cooldown: newCooldown,
          currentNudge: state.nudges.currentNudge?.category === action.category ? null : state.nudges.currentNudge,
        },
      };
    }

    case 'SHOW_NUDGE': {
      const newShown = new Set(state.nudges.shown);
      newShown.add(action.nudge.id);
      return {
        ...state,
        nudges: {
          ...state.nudges,
          shown: newShown,
          currentNudge: action.nudge,
          nudgesThisSession: state.nudges.nudgesThisSession + 1,
        },
      };
    }

    case 'CLEAR_NUDGE':
      return {
        ...state,
        nudges: { ...state.nudges, currentNudge: null },
      };

    case 'OPEN_ASTOR':
      return {
        ...state,
        conversation: { ...state.conversation, isOpen: true, mode: action.mode },
      };

    case 'CLOSE_ASTOR':
      return {
        ...state,
        conversation: { ...state.conversation, isOpen: false, pendingAction: null },
      };

    case 'SET_PENDING_ACTION':
      return {
        ...state,
        conversation: { ...state.conversation, pendingAction: action.action },
      };

    case 'ADD_MESSAGE':
      return {
        ...state,
        conversation: {
          ...state.conversation,
          messages: [...state.conversation.messages, { ...action.message, timestamp: new Date() }],
        },
      };

    case 'RESTORE_DISMISSED_NUDGES': {
      const restored = new Set(action.categories);
      return {
        ...state,
        nudges: { ...state.nudges, dismissed: restored },
      };
    }

    case 'SET_HIGHLIGHT':
      return {
        ...state,
        overlay: { ...state.overlay, highlightedElement: action.highlight },
      };

    case 'CLEAR_HIGHLIGHT':
      return {
        ...state,
        overlay: { ...state.overlay, highlightedElement: null },
      };

    case 'SET_SPOTLIGHT_TOUR':
      return {
        ...state,
        overlay: {
          ...state.overlay,
          spotlightTour: { tourId: action.tourId, steps: action.steps, milestoneId: action.milestoneId },
        },
      };

    case 'CLEAR_SPOTLIGHT_TOUR':
      return {
        ...state,
        overlay: { ...state.overlay, spotlightTour: null },
      };

    case 'SET_SUGGESTED_ACTIONS':
      return {
        ...state,
        conversation: { ...state.conversation, suggestedActions: action.actions },
      };

    default:
      return state;
  }
}

// ============================================================================
// INITIAL STATE FACTORY
// ============================================================================

function createInitialState(
  portal: AstorContextProviderProps['portal'],
  user: AstorContextProviderProps['user'],
  initialPlan: OnboardingPlan | null | undefined,
  maxNudgesPerSession: number,
  chatConfig?: AstorContextProviderProps['chatConfig'],
): AstorContextState {
  const completedMilestones = new Set<string>();
  if (initialPlan) {
    // Restore from the top-level completedMilestones array (persisted in DB)
    const persistedCompleted = (initialPlan as unknown as { completedMilestones?: string[] }).completedMilestones;
    if (Array.isArray(persistedCompleted)) {
      for (const id of persistedCompleted) {
        completedMilestones.add(id);
      }
    }
    // Also check milestone statuses in the plan JSON itself
    if (Array.isArray(initialPlan.sections)) {
      for (const section of initialPlan.sections) {
        for (const milestone of section.milestones ?? []) {
          if (milestone.status === 'completed') {
            completedMilestones.add(milestone.id);
          }
        }
      }
    }
  }

  return {
    user,
    portal,
    onboarding: {
      plan: initialPlan ?? null,
      completedMilestones,
      currentStep: null,
      dismissed: false,
      completionPercentage: computeCompletionPercentage(initialPlan ?? null, completedMilestones),
      isLoading: false,
      isGenerating: false,
    },
    activity: {
      currentRoute: '',
      routeHistory: [],
      pagesVisited: new Map(),
      actionsPerformed: new Map(),
      errorsThisSession: 0,
      errorsEncountered: [],
      idleSince: null,
      sessionStarted: new Date(),
    },
    nudges: {
      dismissed: new Set(),
      shown: new Set(),
      cooldown: new Map(),
      currentNudge: null,
      nudgesThisSession: 0,
      maxPerSession: maxNudgesPerSession,
    },
    conversation: {
      isOpen: false,
      mode: 'widget',
      messages: [],
      pendingAction: null,
      suggestedActions: [],
    },
    chatConfig: chatConfig ?? null,
    overlay: {
      highlightedElement: null,
      spotlightTour: null,
    },
  };
}

// ============================================================================
// LOCALSTORAGE HELPERS
// ============================================================================

function loadDismissedNudges(userId: string): string[] {
  try {
    const raw = localStorage.getItem(`${LOCALSTORAGE_PREFIX}${userId}`);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === 'string');
    return [];
  } catch {
    return [];
  }
}

function saveDismissedNudges(userId: string, categories: Set<string>): void {
  try {
    localStorage.setItem(`${LOCALSTORAGE_PREFIX}${userId}`, JSON.stringify([...categories]));
  } catch {
    // localStorage may be full or unavailable — degrade gracefully
  }
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function AstorContextProvider({
  portal,
  user,
  children,
  onNavigate,
  onCompleteMilestone,
  onDismissOnboarding,
  onGeneratePlan,
  onSyncActivity,
  onGetNudge,
  onGetSuggestions,
  chatConfig,
  initialPlan,
  maxNudgesPerSession = DEFAULT_MAX_NUDGES_PER_SESSION,
}: AstorContextProviderProps) {
  const [state, dispatch] = useReducer(
    astorReducer,
    { portal, user, initialPlan, maxNudgesPerSession, chatConfig },
    (args) => createInitialState(args.portal, args.user, args.initialPlan, args.maxNudgesPerSession, args.chatConfig),
  );

  // Sync initialPlan prop to state when it arrives after mount
  // (tRPC query resolves after first render, so the initializer ran with null)
  useEffect(() => {
    if (initialPlan && Array.isArray(initialPlan.sections) && !state.onboarding.plan) {
      dispatch({ type: 'SET_PLAN', plan: initialPlan });
    }
  }, [initialPlan, state.onboarding.plan]);

  // Event buffer for batched syncing
  const eventBufferRef = useRef<AstorEvent[]>([]);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Store callbacks in refs to avoid re-render loops (tRPC mutations get new refs each render)
  const onSyncActivityRef = useRef(onSyncActivity);
  onSyncActivityRef.current = onSyncActivity;
  const onGetNudgeRef = useRef(onGetNudge);
  onGetNudgeRef.current = onGetNudge;
  const onGetSuggestionsRef = useRef(onGetSuggestions);
  onGetSuggestionsRef.current = onGetSuggestions;

  // Emit session_start event on mount (trackEvent is stable via refs)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally fire once on mount
  useEffect(() => {
    trackEvent('session_start');
  }, []);

  // Restore dismissed nudges from localStorage on mount
  useEffect(() => {
    if (user?.id) {
      const dismissed = loadDismissedNudges(user.id);
      if (dismissed.length > 0) {
        dispatch({ type: 'RESTORE_DISMISSED_NUDGES', categories: dismissed });
      }
    }
  }, [user?.id]);

  // Persist dismissed nudges to localStorage whenever they change
  useEffect(() => {
    if (user?.id && state.nudges.dismissed.size > 0) {
      saveDismissedNudges(user.id, state.nudges.dismissed);
    }
  }, [user?.id, state.nudges.dismissed]);

  // Emit onboarding_completed when all milestones are done
  useEffect(() => {
    if (state.onboarding.completionPercentage === 100 && state.onboarding.plan) {
      trackEvent('onboarding_completed');
    }
  }, [state.onboarding.completionPercentage]);

  // Fetch nudges + suggestions on route changes (debounced)
  const routeChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const route = state.activity.currentRoute;
    if (!route || !user) return;

    // Don't fetch nudges when chat is open (nudges shouldn't interrupt conversation)
    if (state.conversation.isOpen) return;

    // Debounce 500ms to avoid rapid-fire on route transitions
    if (routeChangeTimerRef.current) clearTimeout(routeChangeTimerRef.current);
    routeChangeTimerRef.current = setTimeout(() => {
      routeChangeTimerRef.current = null;
      const pageContext: PageContextInput = {
        route,
        portal,
        isFirstVisit: !state.activity.pagesVisited.has(route) || state.activity.pagesVisited.get(route)!.count <= 1,
        idleSeconds: state.activity.idleSince
          ? Math.floor((Date.now() - state.activity.idleSince.getTime()) / 1000)
          : 0,
        errorsThisSession: state.activity.errorsThisSession,
      };

      // Fetch nudge (non-critical, fire-and-forget)
      if (onGetNudgeRef.current) {
        onGetNudgeRef
          .current(pageContext)
          .then((nudge) => {
            if (nudge) dispatch({ type: 'SHOW_NUDGE', nudge });
          })
          .catch(() => {});
      }

      // Fetch suggestions (non-critical, fire-and-forget)
      if (onGetSuggestionsRef.current) {
        onGetSuggestionsRef
          .current(pageContext)
          .then((suggestions) => {
            if (suggestions) dispatch({ type: 'SET_SUGGESTED_ACTIONS', actions: suggestions });
          })
          .catch(() => {});
      }
    }, 500);

    return () => {
      if (routeChangeTimerRef.current) {
        clearTimeout(routeChangeTimerRef.current);
        routeChangeTimerRef.current = null;
      }
    };
  }, [state.activity.currentRoute, user, portal, state.conversation.isOpen]);

  // Helper: create an AstorEvent from a type + metadata
  // Uses refs for volatile state to keep the callback stable
  const currentRouteRef = useRef(state.activity.currentRoute);
  currentRouteRef.current = state.activity.currentRoute;

  const createEvent = useCallback(
    (type: AstorEventType, metadata?: Record<string, unknown>): AstorEvent | null => {
      if (!user) return null;
      return {
        type,
        timestamp: new Date().toISOString(),
        userId: user.id,
        businessId: user.businessId,
        portal,
        route: currentRouteRef.current,
        metadata,
      };
    },
    [user, portal],
  );

  // Flush event buffer to server (uses ref to avoid re-render loops)
  const flushEvents = useCallback(async () => {
    if (eventBufferRef.current.length === 0 || !onSyncActivityRef.current) return;
    const events = [...eventBufferRef.current];
    eventBufferRef.current = [];
    try {
      await onSyncActivityRef.current(events);
    } catch {
      // Re-queue events on failure so they can be retried on next flush
      eventBufferRef.current = [...events, ...eventBufferRef.current];
    }
  }, []); // Stable — reads from ref, no deps needed

  // Schedule a single flush timeout. Each timeout fires once and does NOT
  // self-reschedule — new timeouts are only created when trackEvent pushes
  // an event into the buffer.  This avoids infinite-timer loops when test
  // environments use vi.runAllTimersAsync().
  const scheduleFlush = useCallback(() => {
    if (syncTimerRef.current !== null) return; // already scheduled
    syncTimerRef.current = setTimeout(() => {
      syncTimerRef.current = null;
      void flushEvents();
    }, ACTIVITY_SYNC_INTERVAL_MS);
  }, [flushEvents]);

  // Cleanup: clear pending timeout and flush remaining events on unmount
  useEffect(() => {
    return () => {
      if (syncTimerRef.current !== null) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
      // Flush remaining events on unmount
      void flushEvents();
    };
  }, [flushEvents]);

  // ---- Actions ----

  const trackEvent = useCallback(
    (type: AstorEventType, metadata?: Record<string, unknown>) => {
      const event = createEvent(type, metadata);
      if (event) {
        eventBufferRef.current.push(event);
        scheduleFlush();
      }
    },
    [createEvent, scheduleFlush],
  );

  const trackPageView = useCallback(
    (route: string) => {
      dispatch({ type: 'TRACK_PAGE_VIEW', route, timestamp: new Date() });
      trackEvent('page_view', { route });
    },
    [trackEvent],
  );

  const trackAction = useCallback(
    (actionKey: string) => {
      dispatch({ type: 'TRACK_ACTION', actionKey });
      trackEvent('action_performed', { actionKey });
    },
    [trackEvent],
  );

  const trackError = useCallback(
    (error: string) => {
      dispatch({ type: 'TRACK_ERROR', error });
      trackEvent('action_failed', { error });
    },
    [trackEvent],
  );

  const resetIdle = useCallback(() => {
    dispatch({ type: 'RESET_IDLE' });
  }, []);

  const completeMilestone = useCallback(
    (milestoneId: string) => {
      dispatch({ type: 'COMPLETE_MILESTONE', milestoneId });
      trackEvent('milestone_completed', { milestoneId });
      if (onCompleteMilestone) {
        void onCompleteMilestone(milestoneId);
      }
    },
    [onCompleteMilestone, trackEvent],
  );

  const dismissOnboarding = useCallback(() => {
    dispatch({ type: 'DISMISS_ONBOARDING' });
    trackEvent('onboarding_dismissed');
    if (onDismissOnboarding) {
      void onDismissOnboarding();
    }
  }, [onDismissOnboarding, trackEvent]);

  const generatePlan = useCallback(async () => {
    if (!onGeneratePlan) return;
    dispatch({ type: 'SET_GENERATING', isGenerating: true });
    try {
      const plan = await onGeneratePlan();
      dispatch({ type: 'SET_PLAN', plan });
    } catch {
      dispatch({ type: 'SET_GENERATING', isGenerating: false });
    }
  }, [onGeneratePlan]);

  const dismissNudge = useCallback(
    (category: string) => {
      dispatch({ type: 'DISMISS_NUDGE', category });
      trackEvent('nudge_dismissed', { category });
    },
    [trackEvent],
  );

  const showNudge = useCallback(
    (nudge: AstorNudge) => {
      if (state.nudges.nudgesThisSession >= state.nudges.maxPerSession) return;
      if (state.nudges.dismissed.has(nudge.category)) return;
      dispatch({ type: 'SHOW_NUDGE', nudge });
      trackEvent('nudge_shown', { nudgeId: nudge.id, category: nudge.category });
    },
    [state.nudges.nudgesThisSession, state.nudges.maxPerSession, state.nudges.dismissed, trackEvent],
  );

  const clearNudge = useCallback(() => {
    dispatch({ type: 'CLEAR_NUDGE' });
  }, []);

  const openAstor = useCallback(
    (mode: 'widget' | 'fullpage' | 'palette' = 'widget', _prefill?: string) => {
      dispatch({ type: 'OPEN_ASTOR', mode });
      trackEvent('astor_opened', { mode });
    },
    [trackEvent],
  );

  const closeAstor = useCallback(() => {
    dispatch({ type: 'CLOSE_ASTOR' });
  }, []);

  const addMessage = useCallback((message: { id: string; role: 'user' | 'assistant'; content: string }) => {
    dispatch({ type: 'ADD_MESSAGE', message });
  }, []);

  const setPendingAction = useCallback((action: AstorAction | null) => {
    dispatch({ type: 'SET_PENDING_ACTION', action });
  }, []);

  const navigateTo = useCallback(
    (path: string) => {
      trackEvent('astor_navigate_used', { path });
      onNavigate?.(path);
    },
    [onNavigate, trackEvent],
  );

  const highlightElement = useCallback(
    (highlight: HighlightAction) => {
      dispatch({ type: 'SET_HIGHLIGHT', highlight });
      trackEvent('highlight_shown', { selector: highlight.selector });
      // Auto-dismiss highlight after specified duration (or 5s default); 0 = no auto-dismiss
      const autoDismissMs = highlight.duration ?? 5000;
      if (autoDismissMs > 0) {
        setTimeout(() => {
          dispatch({ type: 'CLEAR_HIGHLIGHT' });
        }, autoDismissMs);
      }
    },
    [trackEvent],
  );

  const clearHighlight = useCallback(() => {
    dispatch({ type: 'CLEAR_HIGHLIGHT' });
  }, []);

  const startSpotlightTour = useCallback(
    (tourId: string, steps: SpotlightStep[], milestoneId?: string) => {
      dispatch({ type: 'SET_SPOTLIGHT_TOUR', tourId, steps, milestoneId });
      trackEvent('spotlight_tour_started', { tourId, stepCount: steps.length, milestoneId });
    },
    [trackEvent],
  );

  const clearSpotlightTour = useCallback(() => {
    dispatch({ type: 'CLEAR_SPOTLIGHT_TOUR' });
  }, []);

  const setSuggestedActions = useCallback((actions: Array<{ label: string; action: AstorAction }>) => {
    dispatch({ type: 'SET_SUGGESTED_ACTIONS', actions });
  }, []);

  // ---- Memoized context value ----

  const value: AstorContextValue = useMemo(
    () => ({
      ...state,
      completeMilestone,
      dismissOnboarding,
      generatePlan,
      dismissNudge,
      showNudge,
      clearNudge,
      trackEvent,
      trackPageView,
      trackAction,
      trackError,
      resetIdle,
      openAstor,
      closeAstor,
      addMessage,
      setPendingAction,
      navigateTo,
      highlightElement,
      clearHighlight,
      startSpotlightTour,
      clearSpotlightTour,
      setSuggestedActions,
    }),
    [
      state,
      completeMilestone,
      dismissOnboarding,
      generatePlan,
      dismissNudge,
      showNudge,
      clearNudge,
      trackEvent,
      trackPageView,
      trackAction,
      trackError,
      resetIdle,
      openAstor,
      closeAstor,
      addMessage,
      setPendingAction,
      navigateTo,
      highlightElement,
      clearHighlight,
      startSpotlightTour,
      clearSpotlightTour,
      setSuggestedActions,
    ],
  );

  return <AstorContext.Provider value={value}>{children}</AstorContext.Provider>;
}
